/**
 * The approvals page -- a reading this console makes, not a message it is sent.
 *
 * CHARTER §7.139 ⑤ took the approval message away. What a policy sends now is
 * an ordinary reply that happens to carry options; the contract carries the
 * *shape* and no approval semantics at all. So a card here is assembled from
 * two things this shell found on the ledger and matched by session: a reply
 * with options, and an effect the core is holding. It is a reading, and a
 * reading can fail -- which is why the effects the core is holding are listed
 * on their own below, whether or not a question was found to go with them.
 *
 * Answering the same thing in Telegram is equally valid, so this page never
 * claims to be the only way and never shows anything that is not still open.
 *
 * CHARTER §7.135: pressing a button here does not settle the handle. It submits
 * what the person answered as an ordinary input from this console's seat, and
 * the `approval` port's provider — the one place that knows who the question
 * was put to — settles it. So the card is crossed out when the decision comes
 * back down the tail, never on the click: an answer that did not count must
 * leave the question standing.
 *
 * CHARTER §7.48: a denial is an answer, so it carries its reason; §7.29: the
 * card must show category and effect_class.
 *
 * CHARTER §7.140: what a settled hold says is `ok` and a summary in the
 * holder's own words -- two outcomes, not three. `review_owed` and `expired`
 * were read off `approval.decided`, a row the core stopped writing, so both are
 * gone from this page rather than kept as fields nobody sets.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type ApprovalCard, type HeldEffect } from "../lib/api";
import { approvalsFeed, usePendingApprovals } from "../lib/approvalsFeed";
import { formatTime } from "../lib/format";
import { EmptyState, ErrorState, LoadingState, Section, StatusBadge } from "../shell/ui";
import { AskCard } from "../studio/AskCard";
import { approvalResolution, ownerAsk, toolIndex, type ToolIndex } from "../studio/ask";
import { conversationHref } from "../connections/navigation";

export function ApprovalsPage({
  version,
  onToast,
  ownerMode = true,
}: {
  version: number;
  onToast: (message: string, tone: "good" | "critical") => void;
  ownerMode?: boolean;
}) {
  // The pending list is the one the server pushes (§7.194 ⑤); this page
  // reads for the two things no frame carries, history and held effects,
  // through the same read, so its pending list is that one and not a copy.
  const { pending, error: feedError } = usePendingApprovals();
  const [history, setHistory] = useState<ApprovalCard[]>([]);
  const [held, setHeld] = useState<HeldEffect[]>([]);
  const [readError, setReadError] = useState<unknown>(null);
  const error = readError ?? feedError;
  const [tools, setTools] = useState<ToolIndex>({});

  const load = useCallback(() => {
    approvalsFeed
      .read()
      .then((body) => {
        setHistory(body.history);
        setHeld(body.held);
        setReadError(null);
      })
      .catch(setReadError);
  }, []);

  useEffect(load, [load, version]);
  useEffect(() => {
    if (ownerMode) api.capabilities().then(body => setTools(toolIndex(body.registries))).catch(() => {});
  }, [ownerMode]);

  return (
    <div className="approvals">
      <header className="page-head">
        <h1 className="page-title" tabIndex={-1}>
          待我回应
        </h1>
        <p className="page-note micro">
          先核对操作内容和影响，再决定是否同意。答复后，执行进度和结果会在原对话中更新。
        </p>
      </header>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : pending === null ? (
        <LoadingState label="正在读取待回应事项" />
      ) : pending.length === 0 ? (
        <EmptyState
          title="当前没有待你回应的事项"
          hint="需要确认的新请求会出现在这里。你也可以回到对话继续工作。"
        />
      ) : (
        <ul className="approval-list">
          {pending.map((card) => (
            <li key={card.key}>
              {ownerMode ? <>
                <AskCard card={card} tools={tools} onToast={onToast} onDone={load}
                  disabled={Boolean(card.handle && !card.held_by)} />
                <a className="approval-conversation" href={conversationHref(card.session_id, card.owner_conversation)}>{card.owner_conversation === false ? "去工作区" : "查看原对话"}</a>
              </> : <ApprovalCardView card={card} onToast={onToast} onDone={load} />}
            </li>
          ))}
        </ul>
      )}

      <HeldList held={held} cards={pending ?? []} ownerMode={ownerMode} tools={tools} />

      {history.length ? (
        <Section title="最近结束的请求" meta={`${history.length} 条`}>
          <p className="page-note micro">这里记录请求的结束状态；是否执行完成，请查看原对话中的结果。</p>
          <ul className="approval-history">
            {history.map((card) => (
              <li key={card.key} className="approval-history-row">
                <ResolutionBadge card={card} />
                <span className="approval-history-title">{ownerMode ? ownerAsk(card, tools).title : card.title}</span>
                {/* The summary off `effect.released` -- the holder's own account
                    of how the hold ended.  It is the whole of why a refusal was
                    a refusal now that there is no verdict word to read, so a row
                    without it says only that something ended. */}
                {!ownerMode && card.reason ? (
                  <span
                    className="micro approval-history-reason"
                    data-testid="approval-history-reason"
                    title={card.reason}
                  >
                    {card.reason}
                  </span>
                ) : null}
                <time className="micro">{formatTime(card.resolved_at)}</time>
                {ownerMode ? <a href={conversationHref(card.session_id, card.owner_conversation)}>{card.owner_conversation === false ? "去工作区" : "查看原对话"}</a> : null}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}

/**
 * What the core is actually holding, joined to nothing (§7.77).
 *
 * A card is a question somebody asked; this is the effect that is stuck. They
 * are matched by session, because nothing on a reply names an effect -- so the
 * match can miss, and when it does the effect still has to be visible. An
 * effect nobody can see is worse than one shown without its words.
 *
 * Which is the whole of what it is for, and is why the Owner's view lists only
 * the ones no card speaks for. It used to list them all, so the same four
 * effects the cards above were asking about appeared again underneath as
 * 「一项待处理的操作 / 执行操作 / 只是看看」 -- the category and the level in
 * placeholder words, with no question and nothing to press (D666). The developer
 * view keeps the full list: there it is the audit of what the core holds, ids
 * and holder and all.
 */
function HeldList({ held, cards, ownerMode, tools }: {
  held: HeldEffect[]; cards: ApprovalCard[]; ownerMode: boolean; tools: ToolIndex;
}) {
  const spoken = new Set(cards.map((card) => card.effect_id).filter(Boolean));
  const shown = ownerMode ? held.filter((effect) => !spoken.has(effect.effect_id)) : held;
  if (!shown.length) return null;
  return (
    <Section title="仍在等待的操作" meta={`${shown.length} 项`}>
      <p className="page-note micro">
        {ownerMode
          ? "这些操作还没有对应的确认卡，在这一页回答不了。回到原对话可以看到它们停在哪一步。"
          : "这些操作尚未继续。请先处理对应的确认卡；没有卡片的操作，可以回到原对话查看原因。"}
      </p>
      <ul className="held-list" data-testid="held-effects">
        {shown.map((effect) => (
          <li key={effect.effect_id} className="held-row">
            <StatusBadge tone="warning" label="等待处理" />
            {ownerMode
              /* The act in the tool's own declared Chinese, and when it does not
                 declare one, what kind of act it is -- never the key, and never
                 a placeholder that fits anything (D666). */
              ? <span>{heldAct(effect, tools)}</span>
              : <code className="mono">{effect.tool ?? "未知工具"}</code>}
            {ownerMode ? null : <span className="micro">{categoryLabel(effect.category, ownerMode)}</span>}
            {effect.level && !ownerMode ? <LevelChip level={effect.level} ownerMode={ownerMode} /> : null}
            {/* Who is holding it.  The only party allowed to let go (§7.140), so
                a row that cannot name one is a row nobody can release -- said
                plainly rather than left off. */}
            {!ownerMode ? <span className="micro" data-testid="held-by">
              {effect.held_by || "处理来源未记录"}
            </span> : null}
            {spoken.has(effect.effect_id) || ownerMode ? null : (
              <span className="audit-flag" title="请回到原对话查看这项操作的最新状态">
                尚无对应的确认卡
              </span>
            )}
            {!ownerMode ? <code className="mono held-id">{effect.effect_id}</code> : null}
            {ownerMode && effect.session_id ? <a href={conversationHref(effect.session_id, effect.owner_conversation)}>{effect.owner_conversation === false ? "去工作区" : "查看原对话"}</a> : null}
            <time className="micro">{formatTime(effect.ts)}</time>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/**
 * What this held effect is, for an Owner: the act, or the kind of act.
 *
 * Three answers in order, and the shell picks the first one that exists: the
 * act the tool declared for the owner (`ToolSpec.owner.act`, §7.183), the
 * category's act -- which says which question it would have been -- and then
 * that it is waiting and nothing said why.
 *
 * Never the `description`. It used to be the second of these, cut to twenty
 * characters, and the cut of a sentence written for the model is what put 「读取
 * 本实例的发布队列」 on a real owner's card (D780). A tool that has not declared
 * reads as its category here, which is honest and is what makes the missing
 * declaration visible.
 */
function heldAct(effect: HeldEffect, tools: ToolIndex): string {
  return tools[effect.tool ?? ""]?.act
    || "这一项没有说它要做什么";
}

export function ResolutionBadge({ card }: { card: ApprovalCard }) {
  return <StatusBadge {...approvalResolution(card)} />;
}

export function ApprovalCardView({
  card,
  compact = false,
  onToast,
  onDone,
}: {
  card: ApprovalCard;
  compact?: boolean;
  onToast?: (message: string, tone: "good" | "critical") => void;
  onDone?: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [showArgs, setShowArgs] = useState(false);
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState("");
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (denying) reasonRef.current?.focus();
  }, [denying]);

  // A card with a machine half whose row does not name the consumer holding it.
  // Only that: a card with *no* machine half is an open question somebody can
  // still answer -- the answer goes back as an ordinary input and whoever asked
  // matches it (§7.135) -- so its buttons stay.
  const orphaned = card.handle !== "" && !card.held_by;

  const decide = async (decision: string, label: string) => {
    setBusy(decision);
    setFailure(null);
    try {
      await api.resolveApproval(card.key, decision, reason.trim() || undefined);
      // "Sent", not "done": what settles this is the decision landing in the
      // ledger, and the card below says so when it does.
      onToast?.(`已回答「${label}」`, "good");
      setDenying(false);
      setReason("");
      onDone?.();
    } catch (err) {
      const failed = ApiError.fromUnknown(err);
      setFailure(failed);
      onToast?.(`${label}失败：${failed.detail}`, "critical");
    } finally {
      setBusy(null);
    }
  };

  if (card.resolved) {
    return (
      <article className="approval approval-resolved" data-testid="approval-resolved">
        <ResolutionBadge card={card} />
        <p className="approval-title">{card.title}</p>
        {card.reason ? (
          <p className="approval-verdict-note micro">
            理由：{card.reason}
            {card.decided_by
              ? ` · ${card.decided_by} 决定的`
              : card.answered_from
                ? ` · ${card.answered_from} 答的，那时还没登记成人`
                : " · 未记录人工答复"}
          </p>
        ) : null}
        {card.failure ? (
          <p className="approval-verdict-note micro">
            核收到的是 <code className="mono">{card.failure.code}</code>：{card.failure.message}
            {card.failure.retryable ? "（可重试）" : "（不可重试）"}
          </p>
        ) : null}
      </article>
    );
  }

  return (
    <article className={`approval${compact ? " approval-compact" : ""}`} data-testid="approval-card">
      <header className="approval-head">
        <StatusBadge tone="warning" label="等你决定" />
        <span className="micro approval-category">{categoryLabel(card.category)}</span>
        {card.asked_by ? (
          <span className="micro approval-asker" data-testid="approval-asker" title="是谁把这个问题折进这一轮的">
            {card.asked_by} 问的
          </span>
        ) : null}
        <EffectClassChip effectClass={card.effect_class} />
        {card.level ? <LevelChip level={card.level} /> : null}
        {card.reoffered ? (
          <span className="audit-flag" title="核重启时这条还没人答，它被重新问了一遍">
            ↻ 重新问的
          </span>
        ) : null}
        <time className="micro">{formatTime(card.ts)}</time>
      </header>

      <p className="approval-title">{card.title}</p>
      {card.summary ? <p className="approval-summary">{card.summary}</p> : null}

      <dl className="approval-facts">
        <div>
          <dt className="micro">工具</dt>
          <dd className="mono">{card.tool ?? "未知"}</dd>
        </div>
        <div>
          <dt className="micro">效果账</dt>
          <dd className="mono">{card.effect_id ?? "无（不派发效果）"}</dd>
        </div>
        <div>
          <dt className="micro">句柄</dt>
          <dd className="mono" data-testid="approval-handle">
            {/* Empty when this card is a question with no effect beside it:
                the reply asked something, and the machine half was not found.
                Said plainly rather than left blank. */}
            {card.handle || "没有（这张卡只有问题，没配上效果）"}
          </dd>
        </div>
        {card.handle ? (
          <div>
            {/* Off `effect.held`: which `tool.before_call` consumer parked this
                call, and the sentence it gave for parking it (§7.140).  Both
                are shown as they were written -- this console has no vocabulary
                of holders and no vocabulary of reasons. */}
            <dt className="micro">挂着它的</dt>
            <dd data-testid="approval-holder">{card.held_by || "行上没写"}</dd>
          </div>
        ) : null}
        {card.detail ? (
          <div>
            <dt className="micro">它给的理由</dt>
            <dd data-testid="approval-hold-reason">{card.detail}</dd>
          </div>
        ) : null}
        {card.turn_id ? (
          <div>
            <dt className="micro">哪一轮</dt>
            <dd className="mono">{card.turn_id}</dd>
          </div>
        ) : null}
      </dl>

      {Object.keys(card.arguments ?? {}).length ? (
        <div className="approval-args">
          <button
            type="button"
            className="btn btn-quiet"
            aria-expanded={showArgs}
            onClick={() => setShowArgs((v) => !v)}
          >
            {showArgs ? "收起参数" : "看参数"}
          </button>
          {showArgs ? (
            <pre className="mono args-dump">{JSON.stringify(card.arguments, null, 2)}</pre>
          ) : null}
        </div>
      ) : null}

      {failure ? (
        <p className="approval-error" role="alert">
          <strong>{failure.title}</strong>
          {failure.detail}
          {failure.hint ? <span className="micro"> {failure.hint}</span> : null}
        </p>
      ) : null}

      {denying ? (
        <div className="approval-reason">
          {/* The box is armed by whichever answer needs a second press, and that
              is not always the refusal, so the label may not call it one: it
              said "拒绝的理由" under 同意. It names the field and says what
              happens to a refusal, without deciding which answer this is. */}
          <label className="micro" htmlFor={`reason-${card.key}`}>
            理由（会原样交给核；拒绝的话，它还会写进这次失败里）
          </label>
          <textarea
            id={`reason-${card.key}`}
            ref={reasonRef}
            className="reason-input"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="可以不写"
          />
        </div>
      ) : null}

      {orphaned ? (
        /* The effect row does not say who is holding the call, so nobody can be
           named as able to let go -- a database written before `held_by`
           existed. Pressing a button here would submit an answer no consumer is
           listening for, so the card says what it knows and stops. */
        <p className="approval-orphan micro" data-testid="approval-unreleasable">
          这一笔卡在核那边，但账上没写是谁挂的——多半是老库里的行。没人能放它走，所以这里不给按钮。
        </p>
      ) : (
        <div className="approval-actions">
          {card.options.map((option, index) => {
            // What the option says about itself, and the only thing it says:
            // `approves` is the plugins' own word for "this is the one that
            // goes ahead" (a reply option).
            // Everything else stops the held call, and stopping it is the
            // answer that carries a reason -- so that is the one that takes a
            // second press. It used to be armed by `value !== "approve"`, a
            // string no plugin has written since §7.139 ⑤: the real value is
            // `"APR-A3F8E6 同意"`, so *both* buttons were armed.
            const goesAhead = option.approves === true;
            const armed = goesAhead || denying;
            return (
              <button
                key={option.value}
                type="button"
                className={index === card.options.findIndex(item => item.approves === true) ? "btn btn-accent" : "btn"}
                disabled={busy !== null}
                onClick={() => {
                  if (!armed) {
                    setDenying(true);
                    return;
                  }
                  void decide(option.value, option.label);
                }}
              >
                {busy === option.value ? "处理中" : armed ? option.label : `${option.label}…`}
              </button>
            );
          })}
          {denying ? (
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => {
                setDenying(false);
                setReason("");
              }}
            >
              算了
            </button>
          ) : null}
        </div>
      )}
    </article>
  );
}

/**
 * ``ApprovalRequest.effect_class`` -- how bad the wrong answer would be.  The
 * vocabulary is fixed at seven by contracts (CHARTER §7.29 and §7.63), ordered by how
 * much warning they earn:
 *   authority_change ≈ destructive > external_send ≈ script_execution
 *                    > file_write > network_read > other
 * authority_change is "who this machine answers to": handed over once, it does
 * not come back, so it shares the top rank with destruction and is shown at the
 * same level.
 * The weight is encoded twice, as colour and as a few-step meter: colour
 * disappears in greyscale, the steps do not.
 * A value outside the vocabulary is not guessed at; it is shown as "impact
 * unknown (raw value)".
 */
const EFFECT_CLASS: Record<string, { label: string; rank: number; tone: string }> = {
  authority_change: { label: "会改变谁说了算", rank: 5, tone: "critical" },
  destructive: { label: "会毁掉东西", rank: 5, tone: "critical" },
  external_send: { label: "会发到外面", rank: 4, tone: "warning" },
  script_execution: { label: "会执行脚本", rank: 4, tone: "warning" },
  file_write: { label: "会写文件", rank: 3, tone: "warning" },
  network_read: { label: "会读网络", rank: 2, tone: "neutral" },
  other: { label: "其它", rank: 1, tone: "neutral" },
};

const RANK_MAX = 5;

function EffectClassChip({ effectClass }: { effectClass: string }) {
  const hit = EFFECT_CLASS[effectClass];
  const label = hit ? hit.label : `影响不明（${effectClass}）`;
  const meter = hit
    ? "▮".repeat(hit.rank) + "▯".repeat(RANK_MAX - hit.rank)
    : "?????";
  // The meter is drawn with characters, so without this it reaches a screen
  // reader as five box glyphs and reaches everyone else as an unexplained row
  // of bars. The chip says what it means in one sentence, in both places.
  const meaning = hit
    ? `影响分量 ${hit.rank}/${RANK_MAX}（实心方块越多影响越大）：${label}`
    : `影响不明：${effectClass} 不在 effect_class 词表里，分量画不出来`;
  return (
    <span
      className={`effect-class tone-${hit?.tone ?? "unknown"}`}
      data-testid="effect-class"
      data-effect-class={effectClass}
      data-rank={hit?.rank ?? ""}
      role="img"
      aria-label={meaning}
      title={meaning}
    >
      <span className="effect-meter" aria-hidden="true">
        {meter}
      </span>
      {label}
    </span>
  );
}

/**
 * The approval categories this shell has words for.  There is no identity
 * category: changing who this machine listens to is the widest permission_grant
 * there is.
 *
 * `reply` is the seventh and it is this console's own: `workbench.reply` is an
 * `external_send`, so a gate is entitled to hold one, and a card that could not
 * name the question would have shown the bare machine word `reply` to somebody
 * being asked whether to let a message out.
 */
function categoryLabel(category: string, ownerMode = false): string {
  return (
    {
      permission_grant: "授权",
      paid_operation: "付费操作",
      memory_write: "长期记忆写入",
      tool_effect: "执行操作",
      skill_change: "更新技能",
      runtime_control: "调整运行设置",
      reply: "发送回复",
    }[category] ?? (ownerMode ? "操作类型待确认" : category)
  );
}

/**
 * `EffectLevel` -- how much of the world it touches, beside effect_class's *what
 * kind of thing it is*.  Both are needed: a write and a paid call are not the
 * same question.
 */
const LEVEL_LABEL: Record<string, string> = {
  read: "只是看看",
  write: "改本机",
  external: "会到别人那儿",
  paid: "要花钱",
};

function LevelChip({ level, ownerMode = false }: { level: string; ownerMode?: boolean }) {
  return (
    <span className="approval-level micro" data-testid="approval-level" data-level={level}>
      {LEVEL_LABEL[level] ?? (ownerMode ? "影响范围待确认" : level)}
    </span>
  );
}
